import * as am5 from '@amcharts/amcharts5';

import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    InputSignal,
    NgZone,
    OnDestroy,
    Output,
    PLATFORM_ID,
    inject,
    Signal,
    ViewChild,
    computed,
    effect,
    input,
} from '@angular/core';
import { ContractTypeEnum, EquipmentTypeEnum, Quote, Rate } from '../models';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import { GraphBuilder, IqGraphRatesStateService } from '../state';

@Component({
    selector: 'iq-graph-rates-thirty-five-day-forecast',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './iq-graph-rates-thirty-five-day-forecast.component.html',
    styleUrl: './iq-graph-rates-thirty-five-day-forecast.component.scss',
})
export class IqGraphRatesThirtyFiveDayForecastComponent implements OnDestroy {
    id: InputSignal<string> = input('');
    equipmentType: InputSignal<EquipmentTypeEnum> = input(
        EquipmentTypeEnum.Reefer,
    );
    origin: InputSignal<string> = input('');
    destination: InputSignal<string> = input('');
    perMile: InputSignal<boolean> = input(false);
    quote: InputSignal<Quote> = input({ fuel: 0 });
    fetchedSpotRates: Rate[];
    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    vizTime: InputSignal<string> = input('');
    spotRates: Signal<Promise<Rate[]>> = computed(async (): Promise<Rate[]> => {
        // Contract Rates not available for 35 Day forecast
        const rates = await this._state.getThirtyFiveDayForecastRates(
            this.origin(),
            this.destination(),
            this.equipmentType(),
            ContractTypeEnum.Spot,
            this.perMile(),
            this.quote(),
        );

        return rates;
    });

    rootId = computed(() => {
        return `graph-root-35-days-forecast-spot`;
    });
    @ViewChild('graph') container: ElementRef;

    loading: boolean = false;

    root;
    constructor(
        private readonly _state: IqGraphRatesStateService,
        @Inject(PLATFORM_ID) private platformId,
        private zone: NgZone,
        private _graphBuilder: GraphBuilder,
    ) {
        effect(() => {
            if (this.vizTime() === '35DForecast') {
                const dataKey = 'fetchedSpotRates';
                /**
                 * This dispatches an event when the component clicks on a search
                 *
                 *
                 * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
                 *
                 * ```json
                 * {
                 *  "onLoad": [
                 *    {
                 *      "name": "EVENT_NAME",
                 *      "payload": {},
                 *      "sourceEventRule": {
                 *          "payload": [
                 *            {
                 *                "path": "id",
                 *                "operator": "eq",
                 *                "value": "COMPONENT_ID"
                 *            },
                 *            {
                 *                "path": "componentName",
                 *                "operator": "eq",
                 *                "value": "IqGraphRatesThirtyFiveDayForecast"
                 *            },
                 *          ]
                 *      },
                 *      "rules": []
                 *    },
                 *   ],
                 * }
                 * ```
                 */
                const dispatch = () =>
                    this._eventBus.dispatch({
                        name: 'type:load',
                        payload: {
                            id: this.id(),
                            componentName: 'IqGraphRatesThirtyFiveDayForecast',
                            escalationOrigin:
                                this[dataKey]?.[0]?.escalationOrigin,
                            escalationDestination:
                                this[dataKey]?.[0]?.escalationDestination,
                        },
                    });

                dispatch();
            }
        });
        effect(async () => {
            this.loading = true;
            const perMile = this.perMile();
            const spotRates = await this.spotRates();
            this.loading = false;
            this.browserOnly(() => {
                this.clearRoot(this.rootId());
                this.root = this._graphBuilder.create35DayForecastGraph(
                    this.container, 
                    spotRates,
                    perMile,
                );
            });

            this.fetchedSpotRates = spotRates;
            const dataKey = 'fetchedSpotRates';
            /**
             * This dispatches an event when the component clicks on a search
             *
             *
             * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
             *
             * ```json
             * {
             *  "onLoad": [
             *    {
             *      "name": "EVENT_NAME",
             *      "payload": {},
             *      "sourceEventRule": {
             *          "payload": [
             *            {
             *                "path": "id",
             *                "operator": "eq",
             *                "value": "COMPONENT_ID"
             *            },
             *            {
             *                "path": "componentName",
             *                "operator": "eq",
             *                "value": "IqGraphRatesThirtyFiveDayForecast"
             *            },
             *          ]
             *      },
             *      "rules": []
             *    },
             *   ],
             * }
             * ```
             */
            const dispatch = () =>
                this._eventBus.dispatch({
                    name: 'type:load',
                    payload: {
                        id: this.id(),
                        componentName: 'IqGraphRatesThirtyFiveDayForecast',
                        escalationOrigin: this[dataKey]?.[0]?.escalationOrigin,
                        escalationDestination:
                            this[dataKey]?.[0]?.escalationDestination,
                    },
                });

            dispatch();
        });
    }

    browserOnly(f: () => void) {
        if (isPlatformBrowser(this.platformId)) {
            this.zone.runOutsideAngular(() => {
                f();
            });
        }
    }

    clearRoot(rootId) {
        am5.array.each(am5.registry.rootElements, function (r) {
            if (r?.dom?.id == rootId) {
                r.dispose();
            }
        });
    }

    ngOnDestroy() {
        // Clean up root element when the component is removed
        this.browserOnly(() => {
            const root = this.rootId();
            this.clearRoot(root);
        });
    }
}
